/**
 * Calendar arithmetic for the chart and its dialog, done by moment rather than by hand.
 *
 * The hand-written versions this replaces each broke on a date nobody tried: a Sunday was moved into
 * the following week (getDate() - getDay() + 1, with Sunday as 0), a month end taken with setMonth
 * before setDate turned 31 January into 28 February and 31 May's quarter into May, and "one month
 * before 31 October" was 1 October. moment's startOf, endOf and subtract are calendar-aware and clamp
 * at a month's end, which is the established way to do this.
 *
 * Weeks start on Monday, as the procs count them (SET DATEFIRST 1), which is moment's isoWeek.
 */
import * as moment from 'moment';
import { LOCALE } from './format';

/** One bar is one of these. */
export type Period = 'h' | 'd' | 'w' | 'm' | 'q' | 'y';

const UNIT: Record<Period, moment.unitOfTime.StartOf> = {
  h: 'hour', d: 'day', w: 'isoWeek', m: 'month', q: 'quarter', y: 'year'
};

const DURATION: Record<Period, moment.unitOfTime.DurationConstructor> = {
  h: 'hour', d: 'day', w: 'week', m: 'month', q: 'quarter', y: 'year'
};

/** The first moment of the period that holds `date`. */
export function startOfPeriod(date: Date, period: Period): Date {
  return moment(date).startOf(UNIT[period]).toDate();
}

/** The last second of the period that holds `date`, as SSRS sent it: 23:59:59 on its last day. */
export function endOfPeriod(date: Date, period: Period): Date {
  return moment(date).endOf(UNIT[period]).milliseconds(0).toDate();
}

/** The start of the next period: the exclusive end of the one starting at `start`. */
export function nextPeriodStart(start: Date, period: Period): Date {
  return moment(start).startOf(UNIT[period]).add(1, DURATION[period]).toDate();
}

/** `count` periods before `date`, clamped at a month's end: one month before 31 October is 30 September. */
export function subtractPeriods(date: Date, count: number, period: Period): Date {
  return moment(date).subtract(count, DURATION[period]).toDate();
}

/**
 * Sorted bucket keys with any whole periods missing between neighbours put back.
 *
 * The procs only return a bucket that has readings, so a week a meter was offline came back as no
 * week at all and its neighbours closed up over it: 29 June sat next to 13 July with nothing to say a
 * week was gone. A time axis keeps the gap (Highcharts' and ECharts' datetime axes both do), so the
 * missing keys are added and drawn as empty slots.
 *
 * Only a gap that is a whole number of steps is filled. A bucket the server cut short, such as a
 * drilled month's first week starting on a Thursday, is left alone rather than forced onto a grid of
 * its own. Steps are counted from the earlier key so month ends cannot drift, and a key that is
 * already there (the repeated hour when the clocks go back) is not added twice. Undated keys pass
 * through untouched.
 */
export function fillGaps(keys: string[], period: Period, limit = 5000): string[] {
  const out: string[] = [];
  const seen = new Set(keys);
  for (let i = 0; i < keys.length; i++) {
    out.push(keys[i]);
    const a = keyDate(keys[i]);
    const b = i + 1 < keys.length ? keyDate(keys[i + 1]) : null;
    if (!a || !b) continue;

    const missing: string[] = [];
    let n = 1;
    let c = moment(a).add(n, DURATION[period]);
    while (c.valueOf() < b.getTime() && out.length + missing.length < limit) {
      const key = bucketKey(c.toDate(), period)!;
      if (!seen.has(key)) { missing.push(key); seen.add(key); }
      c = moment(a).add(++n, DURATION[period]);
    }
    if (c.valueOf() === b.getTime()) out.push(...missing);
    else missing.forEach(k => seen.delete(k));
  }
  return out;
}

/** The endpoint's date format: local time, never UTC. */
export function stamp(date: Date): string {
  return moment(date).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * A bucket's key, straight off the server's own date text: "2026-08-17" for a day or longer, and
 * "2026-08-17 13:00" for an hour.
 *
 * Read off the text rather than through a Date, because a Date is a moment in the browser's time zone.
 * The readings are wall-clock times with no zone, and on the morning the clocks go forward a reading
 * stamped 01:00 does not exist in Dublin: new Date('2026-03-29T01:00') is 02:00, so it took the 02:00
 * bar's key and that bar was drawn twice its height.
 */
export function bucketKey(raw: string | Date | null | undefined, period: Period): string | null {
  if (raw == null) return null;
  const text = raw instanceof Date ? stamp(raw) : String(raw);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}))?/.exec(text);
  if (!m) return null;
  const day = `${m[1]}-${m[2]}-${m[3]}`;
  return period === 'h' ? `${day} ${m[4] ?? '00'}:00` : day;
}

/** A bucket key back to the local date it names. Built from its parts, so it is never read as UTC. */
export function keyDate(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):00)?$/.exec(key);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0);
}

const dates = new Map<string, Intl.DateTimeFormat>();

/** A date written in en-IE, with the options cached. */
export function formatDate(date: Date, options: Intl.DateTimeFormatOptions): string {
  const key = JSON.stringify(options);
  let f = dates.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCALE, options);
    dates.set(key, f);
  }
  return f.format(date);
}

/** The bucket a bar stands for, in full: "Mon 12 Oct 2026", "Week of 12 Oct 2026", "Q4 2026". */
export function bucketTitle(date: Date, period: Period): string {
  switch (period) {
    case 'h': return `${formatDate(date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} ${moment(date).format('HH:00')}`;
    case 'd': return formatDate(date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    case 'w': return `Week of ${formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    case 'm': return formatDate(date, { month: 'long', year: 'numeric' });
    case 'q': return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
    default: return String(date.getFullYear());
  }
}
